import {Team, useApiCall, useApiEndpoint} from "@jane/lib/src/api"
import React, {FC, ReactNode, useMemo, useState} from "react"
import {config} from "../../config";
import {useNavigate} from "react-router-dom";
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { PageHeader } from "@jane/lib/src/components/content/PageHeader";
import {Card} from "../../../../lib/src/components/Card";
import {Input} from "@jane/lib/src/components/form/Input";
import {Button} from "@jane/lib/src/components/form/Button";
import {faCheckCircle, faSquareCheck, faWarning} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircle} from "@fortawesome/pro-regular-svg-icons";
import {Callout} from "../../../../lib/src/components/content/Callout";
import {useTeamData} from "../../resources/TeamContext";
import {usePermissions} from "../../resources/PermissionsHook";
import {useTeamMap} from "../../components/team-select";
import {Select} from "@jane/lib/src/components/form/Select";
import {faSquare} from "@fortawesome/free-regular-svg-icons";
import {LimitFilter, limitFilterOptions} from "../../../../lib/src/api/repositories/broadcast-repository";

export default function BroadcastsCreatePage(): ReactNode {
  const {broadcasts} = useApiCall(config)
  const navigate = useNavigate()
  const { teams } = useTeamData()
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [targetType, setTargetType] = React.useState<"employees"|"clients">("employees")
  const [targetTeamId, setTargetTeamId] = React.useState<number|null>(null)
  const [coverImage, setCoverImage] = useState<File|null>(null)
  const [attachment, setAttachment] = useState<File|null>(null)

  const [limitToGroup, setLimitToGroup] = useState<boolean>(false)
  const [limitFilter, setLimitFilter] = useState<LimitFilter>('all')

  const onSubmit = async (): Promise<void> => {
    if (targetTeamId === null) {
      return;
    }
    await broadcasts.create(
      subject,
      message,
      coverImage,
      targetType,
      targetTeamId,
      attachment,
      limitFilter
    ).then((b) => {
      navigate(`/broadcasts/${b.id}`)
    })
  }

  return <ContentContainer>
    <PageHeader>Nieuwe Publicatie</PageHeader>
    <div className={'grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4'}>


      <Card title={'1. Inhoud'}>
        <div className={'flex flex-col space-y-3'}>
          <Input type={'text'} label={'Onderwerp'} value={subject} onChange={setSubject} />
          <Input type={'textarea'} height={'64'} label={'Bericht'} value={message} onChange={setMessage} />
        </div>
      </Card>


      <Card title={'2. Ontvangers'}>
        <div className={'flex flex-col space-y-3'}>
          <div className={"grid grid-cols-2 gap-4"}>
            <label className={`border flex items-center border-brand-primary border-opacity-30 ${targetType === "employees" ? 'bg-brand-primary text-brand-on-primary' : ''} cursor-pointer px-3 py-2 rounded-full`}>
              <input type="radio" className={'hidden'} name={"targetType"} value={"employees"} checked={targetType === "employees"} onChange={() => {setTargetType('employees'); }} />
              <FontAwesomeIcon icon={targetType === "employees" ? faCheckCircle : faCircle} className={'mr-2'} />
              Medewerkers
            </label>
            <label className={`border flex items-center border-brand-primary border-opacity-30 ${targetType === "clients" ? 'bg-brand-primary text-brand-on-primary' : ''} cursor-pointer px-3 py-2 rounded-full`}>
              <input type="radio" className={'hidden'} name={"targetType"} value={"clients"} checked={targetType === "clients"} onChange={() => {setTargetType('clients'); }} />
              <FontAwesomeIcon icon={targetType === "clients" ? faCheckCircle : faCircle} className={'mr-2'} />
              Cliënten
            </label>
          </div>
          <BroadcastTeamSelect teams={teams} value={targetTeamId} onChange={(team) => setTargetTeamId(team)} />
          {targetTeamId === null && <>
            <Callout color={'warning'} icon={faWarning}>Selecteer eerst een team om verder te gaan</Callout>
          </>}
          {targetType === 'employees' && <>
            {targetTeamId !== null && <div className={'flex flex-row space-x-4 items-center px-2 py-1 -mx-2 rounded-md hover:bg-brand-200 hover:cursor-default'}
              onClick={() => setLimitToGroup(!limitToGroup)}>
              <FontAwesomeIcon icon={limitToGroup ? faSquareCheck : faSquare}/>
              <div>Beperken tot groep</div>
            </div>}
            {limitToGroup && <div>
              <Select label={'Beperken tot:'} options={Object.entries(limitFilterOptions)} value={limitFilter} onChange={(e) => setLimitFilter(e as keyof typeof limitFilterOptions)} />
            </div>}
          </>}
        </div>
      </Card>

      <div className={'col-span-2'}>
        <Card title={'3. Cover image'}>
          <div>
            <input type={'file'} onChange={(event) => setCoverImage(event.target.files?.[0] ?? null)} />
          </div>
          {coverImage && <div>
            <div className={'text-sm font-medium text-brand-900 mb-1 mt-4'}>Voorbeeld cover</div>
            <img src={`${URL.createObjectURL(coverImage)}`} alt="" className={'w-full h-64 object-cover rounded-lg'} />

            <div className={'text-sm font-medium text-brand-900 mb-1 mt-4'}>Voorbeeld thumbnail</div>
            <img src={`${URL.createObjectURL(coverImage)}`} alt="" className={'w-64 h-64 object-cover rounded-lg'} />

            <div className={'mt-4'}>
              <Button type={'danger'} size={'sm'} text={'Cover image verwijderen'} onClick={() => setCoverImage(null)} />
            </div>
          </div>}
        </Card>
      </div>


    </div>
    <Button type={'primary'} size={'sm'} text={'Opslaan als concept'} disabled={targetTeamId === null || subject.trim() === '' || message.trim() === ''} onClick={() => onSubmit()} />
  </ContentContainer>
}

export const BroadcastTeamSelect: FC<{teams: Team[], value: number|null, onChange: (t: number|null) => void}> = ({teams, value, onChange}) => {

  const { teams: teamsApi } = useApiCall(config)
  const { canListTeams } = usePermissions()
  const { resource: teamResource } = useApiEndpoint(() => teamsApi.myTeam(), canListTeams)
  const { resource: secondaryTeams } = useApiEndpoint(() => teamsApi.myTeams(), canListTeams)

  const teamMap = useTeamMap(teams, teamResource ?? null, secondaryTeams ?? [])
  const {canListAllTeams} = usePermissions()
  const teamOptionsMap: Map<number, [string, number]> = useMemo(() => {
    return new Map([
      [-1, ['Alle', 0]],
      ...teamMap
        .map<[Team, number]>(teams => {
          const team = teams.slice(-1)[0]
          return [team, teams.length]
        })
        .map<[number, [string, number]]>(([team, indentation]) => [team.id, [team.name, indentation]])
    ])
  }, [teamMap])
  const teamFilterOptions = useMemo(() => {
    return Array.from(teamOptionsMap).map(([id, [name, indentation]]: [number, [string, number]]): [string, string] => {
      return [id.toString(), (canListAllTeams ? "\xA0\xA0".repeat(indentation) : "") + name]
    })
  }, [teamOptionsMap, canListAllTeams])

  return <Select label={'Team'} options={teamFilterOptions} value={value === null ? '-1' : value.toString()} onChange={(value) => onChange(value === '-1' ? null : Number(value))} />
}

